import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Loader2, CheckCircle2, Share2 } from 'lucide-react'
import confetti from 'canvas-confetti'
import { EncuestaLayout } from '../../components/public/EncuestaLayout'
import { getGraciasData } from '../../api/encuestas.api'
import type { GraciasData } from '../../types'

export function GraciasPage() {
  const { uuid } = useParams<{ uuid: string }>()
  const [data, setData] = useState<GraciasData | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!uuid) return
    let cancelled = false

    async function load() {
      try {
        const result = await getGraciasData(uuid!)
        if (!cancelled) setData(result)
      } catch {
        // silently fail
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => { cancelled = true }
  }, [uuid])

  // Favicon y título
  useEffect(() => {
    if (!data) return
    document.title = `Gracias - ${data.titulo}`
    if (data.imagen) {
      let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']")
      if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link) }
      link.href = data.imagen
    }
    return () => { document.title = 'Tonal' }
  }, [data])

  // Confetti effect
  useEffect(() => {
    if (data) {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
      })
    }
  }, [data])

  const shareUrl = window.location.href
  const shareText = data ? `Acabo de completar la encuesta: ${data.titulo}` : ''

  const handleShare = (platform: 'whatsapp' | 'facebook' | 'twitter') => {
    const urls: Record<string, string> = {
      whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
    }
    window.open(urls[platform], '_blank', 'noopener,noreferrer')
  }

  if (loading) {
    return (
      <EncuestaLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
        </div>
      </EncuestaLayout>
    )
  }

  return (
    <EncuestaLayout>
      <Card className="p-8 md:p-12 text-center space-y-6">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100">
          <CheckCircle2 className="h-10 w-10 text-green-600" />
        </div>

        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            {data?.titulo ? `Gracias por completar "${data.titulo}"` : 'Gracias por tu respuesta'}
          </h1>
          {data?.descripcion && (
            <p className="mt-3 text-gray-600">{data.descripcion}</p>
          )}
        </div>

        {data && (
          <div className="grid grid-cols-2 gap-4 max-w-xs mx-auto">
            <div className="rounded-lg bg-blue-50 p-3">
              <div className="text-2xl font-bold text-blue-600">{data.total_respuestas}</div>
              <div className="text-xs text-blue-500">Respuestas</div>
            </div>
            <div className="rounded-lg bg-purple-50 p-3">
              <div className="text-2xl font-bold text-purple-600">{data.tasa_finalizacion.toFixed(0)}%</div>
              <div className="text-xs text-purple-500">Finalización</div>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <p className="text-sm text-gray-500 flex items-center justify-center gap-1">
            <Share2 className="h-4 w-4" /> Compartir
          </p>
          <div className="flex items-center justify-center gap-2">
            <Button variant="outline" size="sm" onClick={() => handleShare('whatsapp')}>
              WhatsApp
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleShare('facebook')}>
              Facebook
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleShare('twitter')}>
              X / Twitter
            </Button>
          </div>
        </div>

        {data?.footer && (
          <p className="text-xs text-gray-400 pt-4 border-t">{data.footer}</p>
        )}

        <p className="text-xs text-gray-300">
          Tus respuestas han sido registradas de forma segura.
        </p>
      </Card>
    </EncuestaLayout>
  )
}
